// Shared shape for the Daily Input form used by both the mobile
// full-page report (DailyReportPage) and the desktop drawer
// (DailyReportPanel). Keeping the field list here means both surfaces
// render the exact same fields, in the exact same order, from one
// source of truth.

import dayjs from 'dayjs';
import {generateUUID, deterministicUuidFromSeed} from '@/lib/util.ts';

// Admins/SuperAdmins are allowed to backdate a report by up to this many
// days from today; regular (field) users can't edit the date at all and
// always report for today. Previously duplicated as a local constant in
// both DailyReportPage.tsx and DailyReportPanel.tsx (with a "keep in
// sync" comment) — now a single export so nothing can drift out of sync.
// useRunTicketReconciliation.ts also keys its lookback window to this:
// flagging a Daily Report/Run Ticket mismatch older than what can
// actually be backdated would leave a permanently-unfixable warning on
// RunTicketsPage, so the two are deliberately kept equal rather than
// independent numbers.
export const MAX_BACKDATE_DAYS = 7;

/**
 * Offline-queue entityKey for a Daily Report submit. The queue keeps only
 * the LATEST entry per entityKey (see state/offlineQueue.ts), so the key
 * must include the report date: with just the well id, a backdated report
 * that got queued (offline, dropped connection, timeout) was silently
 * replaced by the next report queued for the same well — typically the
 * oldest day of the 7-day window, when a week is backfilled oldest-first.
 * Per well+date means a second edit to the SAME day still replaces the
 * first (latest edit wins), while different days never collide.
 */
export function dailyReportEntityKey(wellId: string, reportDate: string): string {
   return `well-report:${wellId}:${reportDate}`;
}

export interface DailyReportDraft {
   wellId: string;
   reportDate: string; // yyyy-mm-dd
   oilProductionBbls: number | null;
   gasProductionMcf: number | null;
   waterProductionBbls: number | null;
   // BARRELS of BS&W (additive to Oil Production, not a share of it).
   // CHANGED 2026-10-05 (client request): was a percentage (0-100) —
   // barrels add up across tanks/days and match the purchaser's records,
   // percentages don't. The backend field is still named `bswPercent`
   // (it's just a Float, so it stores barrels fine) — reads alias it to
   // `bswBarrels` in the GraphQL selection and toDailyWellReportInput
   // maps it back on write. Once the backend renames it to bswBarrels,
   // delete the alias and that mapping. See
   // docs/daily-report-bsw-percentage-spec.md.
   bswBarrels: number | null;
   casingPressurePsi: number | null;
   tubingPressurePsi: number | null;
   // String, not number — matches DailyWellReport.runTicketNumber (a real
   // String field on the backend) and RunTicket.ticketNumber (also
   // String — see types/well.ts). Purchaser ticket numbers commonly carry
   // a letter prefix, so this is never assumed to be numeric-only.
   runTicketNumber: string | null;
   notes: string;
   // Optional, unlike the fields above — DailyWellReport.hoursOn is a
   // real, nullable backend field (see toDailyWellReportInput's comment).
   // Originally only WellEditPage.tsx's Production & Downtime table
   // collected it; as of the On Hand/On Timer feature below, the mobile
   // DailyReportPage and desktop DailyReportPanel forms both collect and
   // send it too (via hoursOnValueFromMode at save time). Still left
   // unset here rather than added to DAILY_REPORT_FIELDS, since it's
   // presented as the On Hand/On Timer control, not a generic field —
   // toDailyWellReportInput omits it entirely when absent rather than
   // overwriting an existing value with a guessed default.
   hoursOn?: number | null;
   // Chunk 4 of the tank-gauge/pulled-bottoms feature set (2026-09-30) —
   // NOT a DailyWellReport field at all (it's one or more separate
   // TankGaugeReading rows, upserted independently via
   // useLeaseTankGauge's submitGaugeReading), same reason hoursOn above
   // is optional/excluded rather than a generic field. Keyed by tankId
   // rather than a single value since chunk 5b (2026-09-30) generalized
   // useLeaseTankGauge to hand back MULTIPLE tanks for one well — the
   // rare-but-real case where a well's production currently spans two
   // tanks (its usual tank filled up before being pulled, so the excess
   // is going to a second tank) — one field, and one entry here, per
   // tank in useLeaseTankGauge's `tanks` array. DailyReportPage/
   // DailyReportPanel own the effect that recomputes oilProductionBbls
   // as the SUM of every tank's own gauge-delta-times-share contribution
   // (see lib/tankGaugeMapping.ts's
   // computeOilProductionFromGaugeDelta/computeOilProductionFromStartingBalance/
   // computeAllocatedOilProduction). Left undefined/empty on any well/day
   // with no tanks at all (falls back to plain manual entry) — prefilled
   // instead straight from each tank's own existingReading when there
   // are one or more.
   gaugeInchesByTankId?: Record<string, number>;
}

// 'hoursOn' is excluded here too (alongside wellId/reportDate/notes) —
// without this, adding hoursOn to DailyReportDraft silently widened this
// type (computed via keyof) to include 'hoursOn' as a valid generic-field
// key, even though GENERIC_DAILY_REPORT_FIELDS below never actually
// contains an entry for it. That widened type then leaked into
// draft[field.key]'s indexed-access type on both the mobile and desktop
// surfaces — since DailyReportDraft['hoursOn'] is `number | null |
// undefined` (optional) but MobileFieldInput's `value` prop (and the
// desktop TextField's value) expect `number | null`, TS correctly
// rejected the wider type. Fixed 2026-08-18 after this surfaced as a
// real `tsc -b` error post-code-gen.
export type DailyReportFieldKey = Exclude<
   keyof DailyReportDraft,
   'wellId' | 'reportDate' | 'notes' | 'hoursOn' | 'gaugeInchesByTankId'
>;

// Fields rendered by the generic numeric-input loop on both surfaces —
// runTicketNumber is excluded since it now gets its own text field plus
// a Scan Ticket button (see DailyReportPanel/DailyReportPage), not a
// plain numeric TextField/MobileFieldInput.
export type DailyReportNumericField = Exclude<DailyReportFieldKey, 'runTicketNumber'>;

export interface DailyReportFieldMeta {
   key: DailyReportFieldKey;
   label: string;
   unit: string;
}

// Order here drives the order fields render in on both mobile and desktop.
export const DAILY_REPORT_FIELDS: DailyReportFieldMeta[] = [
   {key: 'oilProductionBbls', label: 'Oil Production', unit: 'bbls'},
   {key: 'gasProductionMcf', label: 'Gas Production', unit: 'mcf'},
   {key: 'waterProductionBbls', label: 'Water Produced', unit: 'bbls'},
   {key: 'bswBarrels', label: 'BS&W', unit: 'bbls'},
   {key: 'tubingPressurePsi', label: 'Tubing Pressure', unit: 'psi'},
   {key: 'casingPressurePsi', label: 'Casing Pressure', unit: 'psi'},
   {key: 'runTicketNumber', label: 'Run Ticket Number', unit: ''},
];

// The subset of DAILY_REPORT_FIELDS that the generic numeric-field loop
// actually renders — see the DailyReportNumericField comment above.
export const GENERIC_DAILY_REPORT_FIELDS: (DailyReportFieldMeta & {key: DailyReportNumericField})[] =
   DAILY_REPORT_FIELDS.filter(
      (f): f is DailyReportFieldMeta & {key: DailyReportNumericField} => f.key !== 'runTicketNumber',
   );

// Single-source-of-truth label for the bespoke Run Ticket Number field —
// pulled from the same array above rather than hardcoded a second time.
export const RUN_TICKET_NUMBER_FIELD: DailyReportFieldMeta = DAILY_REPORT_FIELDS.find(
   (f) => f.key === 'runTicketNumber',
)!;

export function emptyDailyReportDraft(wellId: string, reportDate: string): DailyReportDraft {
   return {
      wellId,
      reportDate,
      oilProductionBbls: null,
      gasProductionMcf: null,
      waterProductionBbls: null,
      bswBarrels: null,
      casingPressurePsi: null,
      tubingPressurePsi: null,
      runTicketNumber: null,
      notes: '',
   };
}

// The subset of a raw DailyWellReport row this form actually reads —
// loose enough to match dailyWellReport_AllWhere's generated element
// type without importing it here (avoids a dependency from this shared
// types file back onto the generated graphql module).
export interface DailyWellReportRow {
   id: unknown;
   entryDate: unknown;
   oilProductionBbls?: number | null;
   gasProductionMcf?: number | null;
   saltWaterDisposalBbls?: number | null;
   // See the bswBarrels comment on DailyReportDraft above.
   bswBarrels?: number | null;
   casingPressurePsi?: number | null;
   tubingPressurePsi?: number | null;
   runTicketNumber?: string | null;
   fieldNotes?: string | null;
   hoursOn?: number | null;
   // The definitive well up/down signal as of this report (2026-08-23,
   // per Duane — not Well.downSince, which the server doesn't appear to
   // derive from this and which only ever reflected the current/most
   // recent down period anyway). See mostRecentReport below.
   wellUp?: boolean | null;
}

// Shared prefill mapping — was previously duplicated inline in
// DailyReportPage's existingReport effect. Used there and by
// RunTicketsPage's "Daily Reports missing a Run Ticket" deep link (see
// its handleOpenUnmatchedReport), so a historical report opened from
// either place prefills its real production numbers instead of a blank
// draft that would silently zero them out on save.
export function draftFromDailyWellReport(wellId: string, report: DailyWellReportRow): DailyReportDraft {
   return {
      wellId,
      reportDate: String(report.entryDate).slice(0, 10),
      oilProductionBbls: report.oilProductionBbls ?? null,
      gasProductionMcf: report.gasProductionMcf ?? null,
      waterProductionBbls: report.saltWaterDisposalBbls ?? null,
      bswBarrels: report.bswBarrels ?? null,
      casingPressurePsi: report.casingPressurePsi ?? null,
      tubingPressurePsi: report.tubingPressurePsi ?? null,
      runTicketNumber: report.runTicketNumber ?? null,
      notes: report.fieldNotes ?? '',
      hoursOn: report.hoursOn ?? null,
   };
}

/** Same mapping as draftFromDailyWellReport above, but blanks the Notes
 * field when the report is currently a pure status-flip row (see
 * isStatusOnlyNote/WELL_DOWN_STATUS_ONLY_NOTE/RESTART_STATUS_ONLY_NOTE
 * below) instead of handing that placeholder sentinel text to a human as
 * if it were a real note.
 *
 * FIXED 2026-09-15 (Duane): "I made a change to the daily report and the
 * status showed 'Pending'... refreshed the window and the status is
 * still pending. Also a comment is being auto added to the daily
 * report, which I don't think we should do." Both symptoms were the same
 * bug: Restart/Mark Down's sentinel note (e.g. "Well restarted — no
 * production report submitted yet for today.") was prefilled straight
 * into the editable Notes field by the plain draftFromDailyWellReport
 * mapping. A pumper who then added real production numbers without
 * touching Notes submitted that sentinel text back to the server
 * unchanged — visible as an unwanted auto-added comment, AND, since the
 * saved fieldNotes still matched a sentinel exactly, isStatusOnlyNote
 * kept treating the row as a status-only flip forever after — so
 * WellsListPage's hasToday derivation never flipped true and the list
 * stayed on "Pending" no matter how many times the page was refreshed
 * (refreshing re-fetches the same now-permanently-mislabeled row).
 *
 * Only used where a draft is handed to a human to edit — DailyReportPage/
 * DailyReportPanel's own prefill effects, and WellsListPage.tsx's
 * backdated-date drawer / RunTicketsPage.tsx's "missing a Run Ticket"
 * dialog opens. Deliberately NOT used inside toWellStatusReportInput's
 * own reuse of draftFromDailyWellReport below — reusing an existing
 * status-only row on a same-day second flip (e.g. Restart right after
 * Mark Down) needs the raw sentinel preserved, not blanked, so the row
 * keeps reading as status-only until a real Submit Report actually
 * replaces it. */
export function draftForEditing(wellId: string, report: DailyWellReportRow): DailyReportDraft {
   const draft = draftFromDailyWellReport(wellId, report);
   if (isStatusOnlyNote(draft.notes)) {
      // A pure sentinel is placeholder text, blanked. A Well Down row that
      // carries the pumper's downtime reason (see wellDownReasonNote) keeps
      // the reason itself, minus its tag prefix, so it shows up as a
      // normal editable note — and a later real Submit Report no longer
      // reads as a status-only flip.
      draft.notes = notesForEditing(draft.notes);
   }
   return draft;
}

/** The Notes text a human should see/edit for a saved `fieldNotes` value:
 * a pure status-only sentinel is blanked, a Well Down reason note loses
 * its tag prefix (the reason itself is kept), anything else passes
 * through unchanged. Shared by draftForEditing and the Mark Down
 * handlers, which use it to put the just-saved reason into the open
 * form so a following Submit Report can't overwrite it with blank
 * notes. */
export function notesForEditing(fieldNotes: string | null | undefined): string {
   const notes = fieldNotes ?? '';
   if (!isStatusOnlyNote(notes)) return notes;
   return notes.startsWith(WELL_DOWN_REASON_NOTE_PREFIX) ? notes.slice(WELL_DOWN_REASON_NOTE_PREFIX.length) : '';
}

// --- Date-change conflict handling (added 2026-09-18 per Duane, re: the
// tester report of a backdated report silently overwriting/orphaning
// another day's real data — see runticket... no, see
// daily_report_date_change_conflict_2026_09_18 in project memory for the
// full writeup) ---
//
// Three small building blocks shared by DailyReportPage.tsx (mobile),
// DailyReportPanel.tsx (desktop drawer), and WellsListPage.tsx/
// RunTicketsPage.tsx's own onSave handlers, so "what report does this
// date belong to" and "has the user actually changed anything" can't
// drift out of sync across those four call sites the way the original
// bug did (each one had its own slightly-different existingReport/
// existingId lookup — see that project memory entry for exactly how they
// disagreed).

/** "The report for well X on date Y" — the one `entryDate` match every
 * caller above used to duplicate inline (some correctly re-deriving it
 * live off the current date field, one keyed off a stale page-level
 * snapshot instead — that mismatch was the actual root cause of a report
 * getting silently relabeled onto the wrong day). Centralized so there's
 * exactly one place this lookup can be wrong. */
export function findReportForDate(reports: DailyWellReportRow[], date: string): DailyWellReportRow | null {
   return reports.find((r) => String(r.entryDate).slice(0, 10) === date) ?? null;
}

/** The draft to show for `date`: the matching real report's data (via
 * draftForEditing — blanks a status-only sentinel note, same as every
 * other place a report gets handed to a human to edit) if one exists,
 * else a blank draft for that date. */
export function resolveDraftForDate(wellId: string, date: string, reports: DailyWellReportRow[]): DailyReportDraft {
   const existing = findReportForDate(reports, date);
   return existing ? draftForEditing(wellId, existing) : emptyDailyReportDraft(wellId, date);
}

/** True when `current`'s actual field values differ from `baseline`'s —
 * i.e. the user has typed/changed something since the draft was last
 * (re)loaded from a real report or reset to blank for a date with none.
 * Deliberately excludes `reportDate`/`wellId` themselves (those are the
 * thing changing, not a user edit to protect) — callers compare the
 * draft against whatever baseline they captured at the last load/reset,
 * not against the previous date's report directly. */
export function isDraftDirty(current: DailyReportDraft, baseline: DailyReportDraft): boolean {
   return (
      current.oilProductionBbls !== baseline.oilProductionBbls ||
      current.gasProductionMcf !== baseline.gasProductionMcf ||
      current.waterProductionBbls !== baseline.waterProductionBbls ||
      current.bswBarrels !== baseline.bswBarrels ||
      current.casingPressurePsi !== baseline.casingPressurePsi ||
      current.tubingPressurePsi !== baseline.tubingPressurePsi ||
      (current.runTicketNumber ?? '') !== (baseline.runTicketNumber ?? '') ||
      current.notes !== baseline.notes ||
      (current.hoursOn ?? null) !== (baseline.hoursOn ?? null) ||
      !sameGaugeInchesByTankId(current.gaugeInchesByTankId, baseline.gaugeInchesByTankId)
   );
}

// Shallow-compares the two gaugeInchesByTankId maps (see
// DailyReportDraft.gaugeInchesByTankId's own comment) — a plain `!==`
// would always be true for two different object references even when
// their contents are identical, which would make isDraftDirty report
// "dirty" on every render for any well/day that has a tank gauge at all.
function sameGaugeInchesByTankId(a?: Record<string, number>, b?: Record<string, number>): boolean {
   const aEntries = Object.entries(a ?? {});
   const bEntries = Object.entries(b ?? {});
   if (aEntries.length !== bEntries.length) return false;
   return aEntries.every(([tankId, value]) => (b ?? {})[tankId] === value);
}

// Shapes the draft into the real `dailyWellReport_CreateUpdate` mutation's
// `DailyWellReportCreateUpdateInput` variables.
//
// `id`: DailyWellReport now carries wellId (and DailyWellReportSearchInput
// can filter by it), so callers can look up "the existing report for this
// well/date" themselves (see DailyReportPage's existingReport query) and
// pass its id in as `existingId` for a true update. When there's no
// existing report — or the caller hasn't looked — a fresh id is generated,
// which is what makes this a create under the hood via the CreateUpdate
// (upsert) mutation.
//
// `waterProductionBbls` maps to `saltWaterDisposalBbls` (renamed on the
// real type); `notes` maps to `fieldNotes`. `barrelTestNotes` has no
// equivalent field on DailyReportDraft yet, so it's left unset.
//
// oilProductionBbls/saltWaterDisposalBbls/tubingPressurePsi/casingPressurePsi
// are `Float!` (NOT NULL) on the real DailyWellReport row — the
// CreateUpdateInput type itself allows null through, but the backend's
// Sequelize model rejects it at the DB layer ("notNull Violation: ...
// cannot be null"). These are exactly the fields MobileFieldInput/the
// desktop drawer disable (and leave null in the draft) while a well is
// down — a down well still needs to be able to submit a notes-only
// report (see the Submit Report button no longer being disabled for
// isDown), so null here is defaulted to 0 rather than passed through.
// gasProductionMcf/runTicketNumber are genuinely nullable on the real
// type, so those are left as-is.
const REQUIRED_NUMERIC_FIELD_DEFAULT = 0;

// `wellUp` (added 2026-08-22, EXPERIMENT per Duane): optional and omitted
// by default so every existing caller (the normal Submit Report flow on
// both DailyReportPage/DailyReportPanel) is unaffected. handleMarkDown in
// both of those files now passes `false` explicitly here instead of the
// old direct `well_CreateUpdate({downSince})` write, to test whether the
// backend auto-derives Well.downSince from this field before committing
// to the wellUp-based redesign (see well_down_wellup_redesign project
// notes). Not wired into the normal per-day submit path yet — that's a
// separate follow-up once the backend behavior is confirmed.
export function toDailyWellReportInput(draft: DailyReportDraft, existingId?: string, wellUp?: boolean) {
   return {
      id: existingId ?? generateUUID(),
      wellId: draft.wellId,
      entryDate: draft.reportDate,
      oilProductionBbls: draft.oilProductionBbls ?? REQUIRED_NUMERIC_FIELD_DEFAULT,
      gasProductionMcf: draft.gasProductionMcf,
      saltWaterDisposalBbls: draft.waterProductionBbls ?? REQUIRED_NUMERIC_FIELD_DEFAULT,
      // Backend input field is still named bswPercent (see DailyReportDraft.bswBarrels).
      bswPercent: draft.bswBarrels,
      casingPressurePsi: draft.casingPressurePsi ?? REQUIRED_NUMERIC_FIELD_DEFAULT,
      tubingPressurePsi: draft.tubingPressurePsi ?? REQUIRED_NUMERIC_FIELD_DEFAULT,
      // FIXED 2026-09-15 (Duane): blanking this box used to send
      // `undefined` (trim ? value : undefined), which Apollo drops from
      // the request entirely — the CreateUpdate upsert only touches
      // fields actually present in the input, so omitting the key left
      // whatever runTicketNumber was already stored untouched instead of
      // clearing it. That's a different bug shape from the fieldNotes fix
      // just below: `undefined` here means "this submit isn't touching
      // the field, leave the existing value as-is," while an explicit
      // `null` means "clear it." Duane's call: blank the box → send
      // `null` (a real, distinct value the field notes fix's `''` isn't
      // needed for, since DailyWellReport.runTicketNumber is a nullable
      // String) so clearing it in the UI and saving actually clears it
      // server-side; a non-blank value still sends the trimmed string.
      runTicketNumber: draft.runTicketNumber?.trim() ? draft.runTicketNumber.trim() : null,
      // FIXED 2026-09-15 (Duane, live-testing on Hartley #3a): this used
      // to omit fieldNotes entirely (send `undefined`, which Apollo drops
      // from the request) whenever the draft's Notes box was blank — the
      // same "trim ? value : undefined" shape runTicketNumber had above
      // before its own fix (see that comment). Confirmed
      // live: a real report submitted right after Restart, with the
      // Notes box genuinely empty, still came back from the server with
      // its OLD sentinel fieldNotes untouched ("Well marked down — no
      // production report submitted yet for today.") — the
      // CreateUpdate upsert only touches fields actually present in the
      // input, so omitting fieldNotes here left the previous value in
      // place instead of clearing it. Now always sends the Notes box's
      // exact current value (including '' to genuinely clear it) —
      // WellEditPage.tsx's saveEditReport/addProduction already pass
      // `notes: report.fieldNotes ?? ''`/`existing?.fieldNotes ?? ''`
      // (explicitly re-supplying the real current value either way), so
      // this can't regress their "carry real notes forward unchanged"
      // behavior — see that file's own comment.
      fieldNotes: draft.notes.trim(),
      // Real, nullable field (DailyWellReport.hoursOn: Int) — omitted
      // (not defaulted to 0/24) when the draft doesn't carry a value, so
      // callers that don't collect it (mobile DailyReportPage, desktop
      // DailyReportPanel) don't silently overwrite an existing value with
      // a guess, and don't start writing a fabricated number for reports
      // nobody actually measured hours for. Only WellEditPage.tsx's
      // Production & Downtime table sets this today (see its
      // ProductionRow comment) — added 2026-08-18 replacing a local-only
      // override cache that was faking this field on the mistaken belief
      // it didn't exist on the backend at all.
      hoursOn: draft.hoursOn ?? undefined,
      wellUp,
   };
}

// --- On Hand / On Timer support (added 2026-08-18 per client request) ---
//
// The daily report now asks whether a well is "On Hand" (running the full
// 24 hours) or "On Timer" (running fewer hours, 1-23/day) — a friendlier
// front-end for the existing hoursOn field, rather than a raw number
// input. A dropdown of 1-23 (not free-typed text) removes the possibility
// of an invalid hoursOn value on the On Timer path entirely.
//
// Per client: both mobile (DailyReportPage) and desktop (DailyReportPanel)
// collect this, it's required, and it defaults to whatever hoursOn the
// well's most recently-reported PRIOR report used — or "On Hand" if the
// well has no prior report at all (or that prior report predates this
// feature and never recorded hoursOn).
export type HoursOnMode = 'onHand' | 'onTimer';

export const ON_HAND_HOURS = 24;

// Selectable values on the On Timer dropdown — 1 through 23.
export const ON_TIMER_HOUR_OPTIONS: number[] = Array.from({length: 23}, (_, i) => i + 1);

/** Derives the On Hand/On Timer mode (and, for On Timer, which hour value
 * to preselect) a stored hoursOn number represents. A missing/null
 * hoursOn — every report saved before this feature existed, or a well
 * with no prior report at all — defaults to On Hand/24, per client
 * request: a well with no hours-per-day ever recorded was implicitly
 * assumed to run around the clock. */
export function hoursOnModeFromValue(hoursOn: number | null | undefined): {mode: HoursOnMode; onTimerHours: number} {
   if (hoursOn != null && hoursOn >= 1 && hoursOn <= 23) {
      return {mode: 'onTimer', onTimerHours: hoursOn};
   }
   return {mode: 'onHand', onTimerHours: ON_TIMER_HOUR_OPTIONS[0]};
}

/** The reverse of hoursOnModeFromValue — the actual hoursOn number to
 * persist (via toDailyWellReportInput) for a given mode/dropdown value. */
export function hoursOnValueFromMode(mode: HoursOnMode, onTimerHours: number): number {
   return mode === 'onHand' ? ON_HAND_HOURS : onTimerHours;
}

/** The well's single most recent report out of a (possibly unsorted,
 * possibly server-pre-filtered) list — used to read the definitive
 * current up/down status straight off DailyWellReport.wellUp
 * (2026-08-23, per Duane: wellUp, not Well.downSince, is what
 * DailyReportPage.tsx/DailyReportPanel.tsx read to decide whether to show
 * the Restart Well button).
 *
 * CHANGED 2026-09-15 (Duane, relaying the backend developer): this used
 * to be the only way to find "most recent" — a plain client-side scan,
 * deliberately not trusting a `sortOrder`+`limit: 1` server-side query,
 * since no confirmed convention for OrderSpec.direction existed anywhere
 * in this codebase yet and guessing wrong would silently return the
 * wrong report rather than erroring. WellsListPage.tsx's "Last Reported"
 * column (2026-09-04) has since proven `sortOrder: [{fieldName:
 * 'entryDate', direction: 'DESC'}]` works in production, and the
 * backend's new entryDateStart/entryDateEnd range args on
 * dailyWellReport_AllWhere make "most recent BEFORE some date" scopeable
 * too — so DailyReportPage.tsx/DailyReportPanel.tsx now fetch just the
 * single already-sorted row they need server-side (sortOrder + limit: 1,
 * optionally entryDateEnd-bounded) instead of this well's entire report
 * history. This function is kept as the null-safe way to read that
 * one-or-zero-row result — still a real scan, not just `reports[0]`, so
 * it stays correct even if a query ever returns more than expected.
 * Its sibling mostRecentPriorReport (which did the "strictly before some
 * date" scan client-side) was removed in the same change — that
 * cutoff moved into the query's entryDateEnd instead. */
export function mostRecentReport(reports: DailyWellReportRow[]): DailyWellReportRow | null {
   let best: DailyWellReportRow | null = null;
   let bestDate = '';
   for (const r of reports) {
      const d = String(r.entryDate).slice(0, 10);
      if (d > bestDate) {
         best = r;
         bestDate = d;
      }
   }
   return best;
}

// --- Backdated Restart support (added 2026-08-07 per client request) ---
//
// Well.downSince only ever tracks the CURRENT (or most recent, if still
// down) down period — restarting clears it to null with no record of
// when. Restart used to always send `downSince: null` with an implicit
// "right now" — if a pumper forgot to click Restart on the actual day a
// well came back up, there was no way to correct that after the fact,
// and every day between the real restart and whenever they finally
// clicked it would still read as "down" once the state's OGP03I "Days
// On" report gets built (days in month minus days down — see the
// DailyWellReport_AllWhere.graphql comment on hoursOn/normalHoursOn/
// operatingStatusOn being exactly the fields that discussion flagged for
// that future calculation).
//
// The fix doesn't need a new backend field: DailyWellReport.operatingStatusOn
// already exists on the schema (just unused everywhere so far). Backdating
// a Restart now backfills one operatingStatusOn:true report per day
// between the chosen restart date and today (exclusive of today, which
// gets its own normal report through the usual flow) for any day that
// doesn't already have a real report — see handleRestart in
// DailyReportPanel.tsx/DailyReportPage.tsx for where this gets called.
// Client confirmed day-level granularity is enough for now — no need to
// track partial-day hoursOn/normalHoursOn beyond the full-day default below.

/** Inclusive-start, exclusive-end list of yyyy-mm-dd date strings. Empty
 * when startDate isn't before endDateExclusive — e.g. a normal
 * (non-backdated) Restart "now" needs no backfill at all. */
export function daysBetween(startDate: string, endDateExclusive: string): string[] {
   const days: string[] = [];
   let cursor = dayjs(startDate);
   const end = dayjs(endDateExclusive);
   while (cursor.isValid() && end.isValid() && cursor.isBefore(end, 'day')) {
      days.push(cursor.format('YYYY-MM-DD'));
      cursor = cursor.add(1, 'day');
   }
   return days;
}

/** A synthetic "well was on this day" report — no production data, just
 * a status correction for a day that was wrongly left looking "down"
 * because Restart wasn't clicked until later. Required NOT NULL numeric
 * fields default to 0, same convention as toDailyWellReportInput above.
 * `wellUp: true` (added 2026-08-23) makes this consistent with the rest
 * of the wellUp ledger — a backfilled day is, by definition, a day the
 * well was actually up. */
export function toBackfillOperatingDayInput(wellId: string, entryDate: string) {
   return {
      id: generateUUID(),
      wellId,
      entryDate,
      oilProductionBbls: REQUIRED_NUMERIC_FIELD_DEFAULT,
      gasProductionMcf: null,
      saltWaterDisposalBbls: REQUIRED_NUMERIC_FIELD_DEFAULT,
      casingPressurePsi: REQUIRED_NUMERIC_FIELD_DEFAULT,
      tubingPressurePsi: REQUIRED_NUMERIC_FIELD_DEFAULT,
      operatingStatusOn: true,
      hoursOn: 24,
      normalHoursOn: 24,
      wellUp: true,
      fieldNotes: 'Backfilled: well was actually back on this day (Restart was backdated).',
   };
}

/** Sentinel DailyWellReport.fieldNotes value written on a fresh (no
 * existing real report for `date` yet) Restart Well status-flip row — see
 * toWellStatusReportInput below. FIXED 2026-09-10 (Duane): "I click
 * restart well ... The well shows in the well list as 'Reported' even
 * though no data was entered. I would think 'Submit Report' would need
 * to be pressed before showing 'Reported'." Before this, a brand-new
 * Restart-created row (all zero-default production numbers, existing
 * only to flip wellUp back to true) was indistinguishable from a real
 * Submit Report to WellsListPage's reportWindowByWellId — the well
 * immediately read as "Reported" the instant Restart Well was clicked.
 * WellsListPage now checks for this exact fieldNotes value and excludes
 * a row carrying it from counting toward hasToday/hasYesterday, the same
 * way toBackfillOperatingDayInput's own fieldNotes explains *that*
 * function's synthetic rows. If the pumper later opens this day's report
 * and actually hits Submit Report, this text prefills the Notes field
 * (same as a backfilled day's note already does today) and is replaced
 * the moment they save — a report's Notes field always ends up being
 * exactly whatever the last real Submit Report for that day wrote there. */
export const RESTART_STATUS_ONLY_NOTE = 'Well restarted — no production report submitted yet for today.';

/** Same sentinel purpose as RESTART_STATUS_ONLY_NOTE above, for the
 * opposite (Mark Well Down) direction. ADDED 2026-09-15 (Duane, relaying
 * a tester): "when I mark a well up, the well status is 'Reported' even
 * though I cancel or don't make changes and submit." The 2026-09-10 fix
 * above only tagged a fresh wellUp:true row, on the reasoning that "a
 * fresh Well Down row (wellUp:false) doesn't need it since isWellDown
 * already makes that well show Down regardless of hasToday" — true only
 * while the well STAYS down. The instant that same well is later
 * Restarted the same day, isWellDown's own Down verdict goes away, and
 * whatever hasToday the earlier Mark-Down row already set (never
 * excluded) is what's left showing — "Reported", with zero real
 * production ever entered. Tagging both directions (see
 * isStatusOnlyNote below) closes this regardless of which order Mark
 * Down/Restart happen in on a given day. */
export const WELL_DOWN_STATUS_ONLY_NOTE = 'Well marked down — no production report submitted yet for today.';

/** True for a DailyWellReport.fieldNotes value that marks a row as a
 * pure up/down status flip (either direction) rather than a real Submit
 * Report — callers (WellsListPage.tsx's hasToday/hasYesterday
 * derivation, ProductionReportPage.tsx's row filter) should check this,
 * not compare against either sentinel constant directly, so both
 * directions stay excluded together. */
export function isStatusOnlyNote(fieldNotes: string | null | undefined): boolean {
   return (
      fieldNotes === RESTART_STATUS_ONLY_NOTE ||
      fieldNotes === WELL_DOWN_STATUS_ONLY_NOTE ||
      (fieldNotes != null && fieldNotes.startsWith(WELL_DOWN_REASON_NOTE_PREFIX))
   );
}

/** ADDED 2026-09-30 (Duane, per the client's email): Well Down now
 * requires a downtime reason, saved in the report's notes. A fresh
 * status-flip row carrying a reason is tagged with this prefix instead of
 * the plain WELL_DOWN_STATUS_ONLY_NOTE sentinel, so it is still
 * recognized as a status-only row (isStatusOnlyNote above — WellsListPage
 * shouldn't read a Mark Down as a real production report) while the
 * reason text itself survives for a human to see and edit
 * (draftForEditing). */
export const WELL_DOWN_REASON_NOTE_PREFIX = 'Well marked down — reason: ';

/** Notes value for a Well Down write carrying `reason`. `existingNotes` is
 * the day's current notes, if a report already exists: real notes are
 * kept with the reason appended (that row is a real report and stays one);
 * blank notes or a leftover status-only placeholder are replaced by the
 * tagged reason note. */
export function wellDownReasonNote(existingNotes: string | null | undefined, reason: string): string {
   const trimmed = reason.trim();
   const existing = (existingNotes ?? '').trim();
   if (existing === '' || isStatusOnlyNote(existing)) return `${WELL_DOWN_REASON_NOTE_PREFIX}${trimmed}`;
   return `${existing}\n\nDown reason: ${trimmed}`;
}

/** Deterministic id for "the one DailyWellReport row that represents
 * well `wellId`'s up/down status on `date`" — see
 * lib/util.ts's deterministicUuidFromSeed for the full reasoning. Only
 * ever actually used as a fallback (see toWellStatusReportInput below);
 * when a real `existing` row is found its own real id is always
 * preferred. */
function dailyWellReportStatusFlipId(wellId: string, date: string): string {
   return deterministicUuidFromSeed(`daily-well-report-status-flip:${wellId}:${date}`);
}

/** Builds a DailyWellReportCreateUpdateInput for a well up/down status
 * flip — Well Down (wellUp:false) or Restart Well (wellUp:true), see
 * handleMarkDown/handleRestart in DailyReportPage.tsx/
 * DailyReportPanel.tsx. Reuses `date`'s existing report if there already
 * is one (never clobbering real production numbers with the
 * zero-defaults toDailyWellReportInput falls back to for a fresh report),
 * else starts from a blank draft. `wellUp` is always passed through
 * explicitly here — this is the one place callers must NOT omit it,
 * since asserting it is the entire point of the write.
 *
 * FIXED 2026-09-10 (Duane), widened 2026-09-15 (see
 * WELL_DOWN_STATUS_ONLY_NOTE's own doc comment): a fresh (no `existing`)
 * status-flip row — either direction — gets tagged with the matching
 * sentinel note so WellsListPage can tell it apart from a real Submit
 * Report. Reusing an *existing* report (either direction) never tags it
 * and never clobbers whatever real fieldNotes it already carries. */
export function toWellStatusReportInput(
   wellId: string,
   date: string,
   reports: DailyWellReportRow[],
   wellUp: boolean,
   downReason?: string,
) {
   const existing = reports.find((r) => String(r.entryDate).slice(0, 10) === date) ?? null;
   const draft = existing ? draftFromDailyWellReport(wellId, existing) : emptyDailyReportDraft(wellId, date);
   if (!existing) draft.notes = wellUp ? RESTART_STATUS_ONLY_NOTE : WELL_DOWN_STATUS_ONLY_NOTE;
   if (!wellUp && downReason != null && downReason.trim() !== '')
      draft.notes = wellDownReasonNote(draft.notes, downReason);
   // FIXED 2026-09-15 (Duane): "For DailyReport we really shouldn't need
   // a separate record for marking a well down and a separate one for
   // well up. There needs only to be one record per day." When `existing`
   // is found this reuses its real id, unchanged — but when it ISN'T
   // found, this used to hand toDailyWellReportInput no id at all,
   // letting it mint a fresh RANDOM one (its own `existingId ??
   // generateUUID()` fallback) every single time. If the caller's own
   // `reports` lookup was stale — a refetch still in flight, or (far more
   // likely in the field) Mark Down and Restart both queued offline back
   // to back before either ever synced, so `reports` never saw the
   // first one before the second ran — each call independently missed
   // `existing` and each minted its OWN random id: two real server rows
   // for one calendar day, exactly what this complaint is about. A
   // deterministic id (same wellId+date always yields the same id)
   // closes this: even on a miss, every call for the same day resolves
   // to the SAME id, so the backend's CreateUpdate (upsert-by-id)
   // updates that one row instead of ever creating a second — whether
   // the two calls happen online seconds apart or get replayed from the
   // offline queue one after the other.
   const fallbackId = dailyWellReportStatusFlipId(wellId, date);
   return toDailyWellReportInput(draft, existing?.id ? String(existing.id) : fallbackId, wellUp);
}
